import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

import { ToastService } from '../../shared/services/toast.service';
import { RegisterMovelFlowService } from '../services/register-movel-flow.service';
import { I18nService } from '../services/i18n.service';

import { PublicGuard } from './public.guard';
import { AuthGuard } from './auth.guard';

@Injectable({
  providedIn: 'root',
})
export class RegisterMovelGuard implements CanActivate, CanActivateChild {
  private router = inject(Router);
  private toastService = inject(ToastService);
  private registerMovelFlow = inject(RegisterMovelFlowService);
  private platformId = inject(PLATFORM_ID);
  private i18nService = inject(I18nService);


  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    return this.evaluateNavigation(route, state);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    return this.evaluateNavigation(childRoute, state);
  }

  private evaluateNavigation(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean | UrlTree {
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }

    // Fluxo de acesso por canal externo não deve ser bloqueado por estados do register-movel
    if (
      state.url.startsWith('/login/cnpj/') ||
      state.url.startsWith('/segment-selection') ||
      state.url.startsWith('/external-channel-complete-address')
    ) {
      return true;
    }

    const navigation = this.router.getCurrentNavigation();
    const trigger = navigation?.trigger;

    const isRegisterMovelRoute = state.url.startsWith('/register-movel');
    const isRegisterStepTwoRoute =
      state.url.startsWith('/register') &&
      route.queryParamMap.get('step') === '2' &&
      route.queryParamMap.get('returnTo') === 'register-movel';

    const routeConfig = route.routeConfig;
    const hasPublicGuard = routeConfig?.canActivate?.some(
      (guard: any) => guard?.name === 'PublicGuard' || guard === PublicGuard
    );
    const hasAuthGuard = routeConfig?.canActivate?.some(
      (guard: any) => guard?.name === 'AuthGuard' || guard === AuthGuard
    );

    const isAllowedPublicRoute = hasPublicGuard;

    const isRegisterRoute = state.url.startsWith('/register');
    const isRegisterInMovelFlow =
      isRegisterRoute &&
      route.queryParamMap.get('returnTo') === 'register-movel';
    const isPublicRegisterRoute =
      isRegisterRoute && !isRegisterInMovelFlow;

    if (trigger === 'popstate') {
      if (isRegisterMovelRoute) {
        this.registerMovelFlow.setStage('cnpj-selection');
      } else {
        this.registerMovelFlow.reset();
      }
      return true;
    }

    const stage = this.registerMovelFlow.getStage();
    if (!stage) {
      return true;
    }

    if (isAllowedPublicRoute || isPublicRegisterRoute) {
      return true;
    }

    if (stage === 'cnpj-selection') {
      if (isRegisterMovelRoute) {
        return true;
      }

      this.toastService.showError(
        this.i18nService.instant(
          'pages.registerMovel.guards.selectCnpj'
        ),
        5000,
        'top-center'
      );
      return this.router.parseUrl('/register-movel');
    }

    if (stage === 'step2') {
      if (isRegisterStepTwoRoute) {
        return true;
      }

      this.toastService.showError(
        this.i18nService.instant(
          'pages.registerMovel.guards.finishRegistration'
        ),
        5000,
        'top-center'
      );
      return this.router.parseUrl(
        '/register?step=2&returnTo=register-movel'
      );
    }

    return true;
  }
}
